import { apiService } from './api';
import type { AppLocale } from '../i18n';

export interface ReleaseChangeSections {
  features: string[];
  modules: string[];
  bugFixes: string[];
  improvements: string[];
}

export interface PublicAppRelease {
  id: number;
  versionLabel: string;
  title?: string | null;
  titleFa?: string | null;
  summary: string;
  summaryFa?: string | null;
  heroImageUrl?: string | null;
  changes: ReleaseChangeSections;
  changesFa?: ReleaseChangeSections | null;
  releasedAt: string;
}

export interface PwaReleaseInfo {
  shellVersion: number;
  activeReleaseId?: number | null;
  versionLabel?: string | null;
  title?: string | null;
  titleFa?: string | null;
  summary?: string | null;
  summaryFa?: string | null;
  heroImageUrl?: string | null;
  changes?: ReleaseChangeSections;
  changesFa?: ReleaseChangeSections | null;
  updateReminderSeconds?: number;
}

const emptyChanges = (): ReleaseChangeSections => ({
  features: [],
  modules: [],
  bugFixes: [],
  improvements: [],
});

const hasChangeDetails = (changes: ReleaseChangeSections): boolean =>
  changes.features.length +
    changes.modules.length +
    changes.bugFixes.length +
    changes.improvements.length >
  0;

const asStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item).trim()).filter(Boolean);
};

/** Normalize release change sections from API (camelCase or PascalCase). */
export const normalizeReleaseChanges = (raw: unknown): ReleaseChangeSections => {
  const source = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    features: asStringArray(source.features ?? source.Features),
    modules: asStringArray(source.modules ?? source.Modules),
    bugFixes: asStringArray(source.bugFixes ?? source.BugFixes),
    improvements: asStringArray(source.improvements ?? source.Improvements),
  };
};

export function pickLocalizedReleaseChanges(
  english?: ReleaseChangeSections | null,
  persian?: ReleaseChangeSections | null,
  locale: AppLocale = 'en'
): ReleaseChangeSections {
  if (locale === 'fa' && persian && hasChangeDetails(persian)) return persian;
  return english ?? emptyChanges();
}

const normalizePublicRelease = (raw: unknown): PublicAppRelease => {
  const source = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const changesFaRaw = source.changesFa ?? source.ChangesFa;
  return {
    id: Number(source.id ?? source.Id ?? 0),
    versionLabel: String(source.versionLabel ?? source.VersionLabel ?? ''),
    title: (source.title ?? source.Title) as string | null | undefined,
    titleFa: (source.titleFa ?? source.TitleFa) as string | null | undefined,
    summary: String(source.summary ?? source.Summary ?? ''),
    summaryFa: (source.summaryFa ?? source.SummaryFa) as string | null | undefined,
    heroImageUrl: (source.heroImageUrl ?? source.HeroImageUrl) as string | null | undefined,
    changes: normalizeReleaseChanges(source.changes ?? source.Changes),
    changesFa: changesFaRaw != null ? normalizeReleaseChanges(changesFaRaw) : null,
    releasedAt: String(source.releasedAt ?? source.ReleasedAt ?? ''),
  };
};

const normalizePwaReleaseInfo = (raw: unknown): PwaReleaseInfo => {
  const source = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const activeReleaseIdRaw = source.activeReleaseId ?? source.ActiveReleaseId;
  const changesFaRaw = source.changesFa ?? source.ChangesFa;
  return {
    shellVersion: Number(source.shellVersion ?? source.ShellVersion ?? 1),
    activeReleaseId:
      activeReleaseIdRaw == null || activeReleaseIdRaw === ''
        ? null
        : Number(activeReleaseIdRaw),
    versionLabel: (source.versionLabel ?? source.VersionLabel) as string | null | undefined,
    title: (source.title ?? source.Title) as string | null | undefined,
    titleFa: (source.titleFa ?? source.TitleFa) as string | null | undefined,
    summary: (source.summary ?? source.Summary) as string | null | undefined,
    summaryFa: (source.summaryFa ?? source.SummaryFa) as string | null | undefined,
    heroImageUrl: (source.heroImageUrl ?? source.HeroImageUrl) as string | null | undefined,
    changes: normalizeReleaseChanges(source.changes ?? source.Changes),
    changesFa: changesFaRaw != null ? normalizeReleaseChanges(changesFaRaw) : null,
    updateReminderSeconds: Number(
      source.updateReminderSeconds ?? source.UpdateReminderSeconds ?? 300
    ),
  };
};

class AppReleaseService {
  async getPublishedReleases(limit?: number): Promise<PublicAppRelease[]> {
    try {
      const query = limit != null ? `?limit=${limit}` : '';
      const releases = await apiService.get<unknown[]>(
        `/api/System/app-releases${query}`,
        { cache: true, cacheTTL: 60_000 }
      );
      return (releases ?? []).map(normalizePublicRelease);
    } catch {
      return [];
    }
  }

  async getPwaReleaseInfo(): Promise<PwaReleaseInfo> {
    try {
      const raw = await apiService.get<unknown>('/api/System/pwa-release-info', {
        cache: false,
      });
      const info = normalizePwaReleaseInfo(raw);

      if (!hasChangeDetails(info.changes ?? emptyChanges()) && info.activeReleaseId) {
        const releases = await this.getPublishedReleases();
        const matched = releases.find((release) => release.id === info.activeReleaseId);
        if (matched) {
          info.changes = matched.changes;
          info.changesFa = matched.changesFa;
          info.title ??= matched.title;
          info.titleFa ??= matched.titleFa;
          info.summary ??= matched.summary;
          info.summaryFa ??= matched.summaryFa;
          info.heroImageUrl ??= matched.heroImageUrl;
          info.versionLabel ??= matched.versionLabel;
        }
      }

      return {
        ...info,
        changes: info.changes ?? emptyChanges(),
      };
    } catch {
      return { shellVersion: 1, changes: emptyChanges() };
    }
  }
}

export const appReleaseService = new AppReleaseService();
